import React, { useState, useCallback } from 'react';
import { Check, ChevronDown, AlertCircle } from 'lucide-react';
import legacyUapiService from '../../services/LegacyUapiService';
import Button from './Button';
import Spinner from './Spinner';
import './UapiModuleCalls.css';

// How much of a module one grant covers.
//
// A grant used to be all-or-nothing: allowing a caller to read a reservation allowed it to create,
// amend and cancel one, because GET, POST, PUT and DELETE on /rsv are the same grant. The coarseness
// pushed the wrong way — rather than refuse a partner who needs one read, /rsv gets granted whole and
// the other 57 operations come with it, unnoticed, because nothing ever said what a module contains.
//
// Used at BOTH levels, from one component rather than a copy per screen:
//   credential  what the property's credential may reach at all      (UapiAccessDetailPage)
//   access      what it may reach through ONE backend system         (UapiAccessEditPage)
// The dispatcher checks one or the other depending on the binding's override_modules, so a narrowing
// that existed at only one level would be half a feature, and the missing half is a way through.
//
// AN ALLOW LIST, NOT A DENY LIST. Both express "some of /rsv" and they differ only on the operation
// nobody has decided about yet — the case that matters, because Oracle's specs are re-imported and
// operations appear without anyone opening this screen. Selected grants refuse them; somebody has to
// choose. A deny list would have granted them silently.
//
// Props:
//   level     'credential' | 'access'
//   module    the grant row — { id, uapi_route_id, path, operation_mode, operations }
//   onSaved   called after a successful save so the parent can reload its list
//   disabled  the parent is busy with something else
export default function UapiModuleCalls({ level, module, onSaved, disabled = false }) {
    const [open, setOpen] = useState(false);
    const [operations, setOperations] = useState(null);
    const [picked, setPicked] = useState([]);
    // The redaction sets choosable under this module, keyed by payload_id — one document is shared by
    // every URL that carries it, so they are not per call.
    const [setsByPayload, setSetsByPayload] = useState({});
    // operation id -> redaction set id. Absent means "resolve by audience", which is the default and
    // what every grant did before a call could name one.
    const [redactions, setRedactions] = useState({});
    const [mode, setMode] = useState(module.operation_mode || 'ALL');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const selected = (module.operation_mode || 'ALL') === 'SELECTED';
    const count = Number(module.operation_count || (module.operations || []).length);

    // Loaded when the panel opens, not with the page: a screen showing a dozen grants would otherwise
    // fetch a dozen operation lists nobody has asked to see.
    const load = useCallback(async () => {
        try {
            setError(null);
            const res = await legacyUapiService.listModuleOperations(module.uapi_route_id);
            const payload = res.data || res;
            const list = payload.operations || [];
            setOperations(list);

            // Grouped once here rather than filtered per row on every render: a module can carry a
            // hundred calls and the list re-renders on every tick.
            const grouped = {};
            for (const set of payload.redactionSets || []) {
                (grouped[String(set.payload_id)] = grouped[String(set.payload_id)] || []).push(set);
            }
            setSetsByPayload(grouped);

            // An ALL grant opens with everything ticked — that is what it currently allows, and starting
            // from an empty list would misrepresent the grant being edited.
            setPicked(selected
                ? (module.operations || []).map(o => String(o.id))
                : list.map(o => String(o.id)));
            // Reopen on what was saved, so a panel opened to change one call does not silently drop the
            // redactions chosen for the others when it is saved again.
            setRedactions(Object.fromEntries((module.operations || [])
                .filter(o => o.redaction_set_id)
                .map(o => [String(o.id), String(o.redaction_set_id)])));
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Could not load this module’s calls');
            setOperations([]);
        }
    }, [module.uapi_route_id, module.operations, selected]);

    const toggle = () => {
        const next = !open;
        setOpen(next);
        setMode(module.operation_mode || 'ALL');
        if (next && operations === null) load();
    };

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            await legacyUapiService.setModuleGrant(level, module.id, {
                mode,
                operations: mode === 'SELECTED'
                    ? picked.map(id => ({
                        id: Number(id),
                        redactionSetId: redactions[String(id)] ? Number(redactions[String(id)]) : null,
                    }))
                    : [],
            });
            setOpen(false);
            if (onSaved) await onSaved();
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Could not save that');
        } finally {
            setBusy(false);
        }
    };

    const all = operations || [];
    const allPicked = all.length > 0 && picked.length === all.length;

    return (
        <>
            <button
                type="button"
                className={`uapi-calls-btn${selected ? ' narrowed' : ''}`}
                onClick={toggle}
                disabled={disabled}
                title={selected
                    ? `${module.path} is granted for ${count} call(s) only`
                    : `${module.path} is granted for every call under it`}
            >
                {selected ? `${count} call${count === 1 ? '' : 's'}` : 'All calls'}
                <ChevronDown size={11} />
            </button>

            {open && (
                <div className="uapi-calls-panel">
                    <div className="uapi-calls-head">
                        <strong>{module.path}</strong>
                        <span className="lu-muted">
                            {operations === null ? 'Loading…' : `${all.length} call${all.length === 1 ? '' : 's'}`}
                        </span>
                    </div>

                    {/* The choice, stated as what it does rather than as a mode name. "Every call"
                        includes ones imported later; a selection does not, deliberately. */}
                    <div className="uapi-calls-modes">
                        <label>
                            <input
                                type="radio"
                                checked={mode === 'ALL'}
                                onChange={() => setMode('ALL')}
                                disabled={busy}
                            />
                            Every call under {module.path}, including any added later
                        </label>
                        <label>
                            <input
                                type="radio"
                                checked={mode === 'SELECTED'}
                                onChange={() => setMode('SELECTED')}
                                disabled={busy}
                            />
                            Only the calls ticked below
                        </label>
                    </div>

                    {mode === 'SELECTED' && (operations === null ? (
                        <div className="uapi-calls-loading"><Spinner size="sm" /></div>
                    ) : all.length === 0 ? (
                        <p className="lu-empty">
                            This module has no operations yet, so there is nothing to choose. Import them
                            from Oracle&rsquo;s spec first.
                        </p>
                    ) : (
                        <>
                            <label className="uapi-calls-row uapi-calls-all">
                                <input
                                    type="checkbox"
                                    checked={allPicked}
                                    ref={(el) => {
                                        // Indeterminate is a property, not an attribute.
                                        if (el) el.indeterminate = picked.length > 0 && !allPicked;
                                    }}
                                    onChange={() => setPicked(allPicked ? [] : all.map(o => String(o.id)))}
                                    disabled={busy}
                                />
                                Select all ({all.length})
                            </label>
                            <div className="uapi-calls-list">
                                {all.map(o => {
                                    const on = picked.includes(String(o.id));
                                    // Only this call's OWN document's sets. Rules address paths in one
                                    // document, so a set from another matches nothing — and matching
                                    // nothing means redacting nothing, which would read as protection
                                    // here while the response went out whole. The server refuses it too.
                                    const sets = setsByPayload[String(o.payload_id)] || [];
                                    return (
                                        <div key={o.id} className="uapi-calls-row-wrap">
                                            <label className="uapi-calls-row">
                                                <input
                                                    type="checkbox"
                                                    checked={on}
                                                    onChange={() => setPicked(prev => (prev.includes(String(o.id))
                                                        ? prev.filter(x => x !== String(o.id))
                                                        : [...prev, String(o.id)]))}
                                                    disabled={busy}
                                                />
                                                <span className={`lu-method lu-method-${String(o.method).toLowerCase()}`}>
                                                    {o.method}
                                                </span>
                                                <code className="lu-mono">{o.full_path}</code>
                                                {o.description && (
                                                    <span className="uapi-calls-desc">{o.description}</span>
                                                )}
                                            </label>

                                            {/* Shown only on a ticked call: a redaction for a call this
                                                grant does not allow is a decision with nothing to apply
                                                to, and the server drops it with the unticked row. */}
                                            {on && (sets.length > 0 ? (
                                                <div className="uapi-calls-redaction">
                                                    <span className="lu-muted">Sees</span>
                                                    <select
                                                        value={redactions[String(o.id)] || ''}
                                                        onChange={(e) => setRedactions(prev => {
                                                            const next = { ...prev };
                                                            if (e.target.value) next[String(o.id)] = e.target.value;
                                                            else delete next[String(o.id)];
                                                            return next;
                                                        })}
                                                        disabled={busy}
                                                    >
                                                        {/* The absence of a choice is a real answer, not
                                                            a blank: it means the redaction addressed to
                                                            this consumer applies — partner, chain, hotel,
                                                            x-hotelid — or the payload default if none is. */}
                                                        <option value="">
                                                            Whatever is addressed to this consumer (default)
                                                        </option>
                                                        {sets.map(set => (
                                                            <option key={set.id} value={String(set.id)}>
                                                                {set.name}
                                                                {set.is_default === 'Y'
                                                                    ? ' · redacts nothing'
                                                                    : ` · ${set.rule_count} rule${Number(set.rule_count) === 1 ? '' : 's'}`}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {/* NONE OF THESE FIT is a normal answer. The list is
                                                        what somebody has already written for this
                                                        document, and the first person to need a
                                                        narrower one was previously stuck: pick a set
                                                        that says the wrong thing, or abandon the grant
                                                        and go hunting for where redactions are authored.
                                                        A new tab, so the grant in progress survives. */}
                                                    <a
                                                        className="uapi-calls-new-redaction"
                                                        href={`/admin/universal-api/payloads/${o.payload_id}/redactions`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title="Define a new redaction for this call's document"
                                                    >
                                                        New…
                                                    </a>
                                                </div>
                                            ) : (
                                                <div className="uapi-calls-redaction">
                                                    <span className="lu-muted">
                                                        {o.payload_id
                                                            ? 'No redactions defined for this call’s document yet'
                                                            : 'No document mapped to this call, so there is nothing to redact'}
                                                    </span>
                                                    {/* Only where there is a document to write rules
                                                        against. Without a payload there is nothing to
                                                        author, and offering it would send the reader to
                                                        a page that cannot help them. */}
                                                    {o.payload_id && (
                                                        <a
                                                            className="uapi-calls-new-redaction"
                                                            href={`/admin/universal-api/payloads/${o.payload_id}/redactions`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            title="Define the first redaction for this call's document"
                                                        >
                                                            Define one…
                                                        </a>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ))}

                    {error && (
                        <p className="uapi-calls-error">
                            <AlertCircle size={12} /> {error}
                        </p>
                    )}

                    <div className="uapi-calls-actions">
                        <Button
                            variant="success"
                            onClick={save}
                            disabled={busy || (mode === 'SELECTED' && picked.length === 0)}
                        >
                            <Check className="icon" /> {busy ? 'Saving…' : 'Save'}
                        </Button>
                        <button
                            type="button"
                            className="pp-back-btn"
                            style={{ margin: 0 }}
                            onClick={() => setOpen(false)}
                            disabled={busy}
                        >
                            Cancel
                        </button>
                        {/* Said here rather than discovered on the first 403: a selection of none allows
                            nothing while the module still reads as granted everywhere else. */}
                        {mode === 'SELECTED' && picked.length === 0 && (
                            <span className="lu-muted">Tick at least one call, or grant the whole module.</span>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
